import { Reveal } from "@components/ui";

const correctionFactors = [
  { mbar: "37 - 300", psig: "0.5", range: "Low Pressure", factor: "1.0" },
  { mbar: "300 – 350", psig: "5", range: "Medium Pressure", factor: "1.3" },
  { mbar: "700", psig: "10", range: "High Pressure", factor: "1.65" },
  { mbar: "1000 (1 bar)", psig: "15", range: "High Pressure", factor: "1.9" },
];

const correctionHeaders = [
  "Pressure (mbar)",
  "Pressure (PSIG)",
  "Range",
  "Correction Factor",
];

const billBreakdown = [
  {
    component: "Gas Consumption",
    description: "10 m³ × 35 AED",
    example: "350.00",
  },
  {
    component: "Billing & Meter Maintenance Fee",
    description: "Fixed",
    example: "20.00",
  },
  {
    component: "Smart Meter Connection Fee (one-time)",
    description: "Fixed",
    example: "25.00",
  },
  {
    component: "VAT (5%)",
    description: "Applied on total",
    example: "19.75",
  },
];

const billHeaders = ["Component", "Description", "Example (AED)"];

export function CorrectionFactorsSection() {
  return (
    <section className="w-full bg-white overflow-hidden py-8 md:py-12 lg:py-[80px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col gap-5 md:gap-6 lg:gap-8">
        {/* Correction Factors Table */}
        <Reveal direction="up">
          <div className="flex flex-col items-start gap-3 md:gap-4">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/8 text-[#122d63] text-[13px] md:text-[15px] font-semibold tracking-[-0.02em] uppercase font-albert-sans">
              Technical Reference
            </span>
            <h2 className="text-[24px] md:text-[40px] lg:text-[54px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
              Correction Factors (Technical Reference)
            </h2>
          </div>
        </Reveal>

        <Reveal direction="up">
          <div className="flex flex-col gap-4 md:gap-5">
            {/* Mobile Cards */}
            <div className="flex flex-col gap-3 md:hidden">
              {correctionFactors.map((row) => (
                <div
                  key={row.mbar}
                  className="rounded-[12px] border border-[#62636C33] p-4 hover:border-[#122d63]/15 hover:shadow-[0_4px_16px_rgba(18,45,99,0.08)] transition-all duration-300"
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[12px] text-[#62636c] font-albert-sans tracking-[-0.04em]">
                        Pressure (mbar)
                      </p>
                      <p className="text-[16px] font-semibold text-[#1c1c1c] font-albert-sans tracking-[-0.04em]">
                        {row.mbar}
                      </p>
                    </div>
                    <div>
                      <p className="text-[12px] text-[#62636c] font-albert-sans tracking-[-0.04em]">
                        Pressure (PSIG)
                      </p>
                      <p className="text-[16px] font-semibold text-[#1c1c1c] font-albert-sans tracking-[-0.04em]">
                        {row.psig}
                      </p>
                    </div>
                    <div>
                      <p className="text-[12px] text-[#62636c] font-albert-sans tracking-[-0.04em]">
                        Range
                      </p>
                      <p className="text-[16px] font-semibold text-[#1c1c1c] font-albert-sans tracking-[-0.04em]">
                        {row.range}
                      </p>
                    </div>
                    <div>
                      <p className="text-[12px] text-[#62636c] font-albert-sans tracking-[-0.04em]">
                        Correction Factor
                      </p>
                      <p className="text-[16px] font-semibold text-[#f24d2b] font-albert-sans tracking-[-0.04em]">
                        {row.factor}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block rounded-t-[16px] border border-[#62636C33] overflow-hidden">
              <div className="bg-[#f24d2b] grid grid-cols-4">
                {correctionHeaders.map((header) => (
                  <div
                    key={header}
                    className="py-[12px] px-[15px] border-r border-white last:border-r-0"
                  >
                    <p className="text-[20px] lg:text-[28px] tracking-[-0.04em] font-medium text-white font-albert-sans leading-[40px]">
                      {header}
                    </p>
                  </div>
                ))}
              </div>
              {correctionFactors.map((row) => (
                <div
                  key={row.mbar}
                  className="grid grid-cols-4 border-t border-[#62636C33] hover:bg-[#f8f9fb] transition-colors duration-200"
                >
                  {[row.mbar, row.psig, row.range, row.factor].map(
                    (cell, i) => (
                      <div
                        key={i}
                        className="py-[8px] px-[15px] border-r border-[#62636C33] last:border-r-0"
                      >
                        <p className="text-base lg:text-[24px] tracking-[-0.04em] leading-[32px] text-[#62636c] font-albert-sans">
                          {cell}
                        </p>
                      </div>
                    )
                  )}
                </div>
              ))}
            </div>

            {/* Formula Box */}
            <div className="rounded-[12px] md:rounded-[16px] bg-[#f6f6f9] flex flex-wrap items-center gap-2 md:gap-6 px-4 md:px-[48px] py-4 md:py-[24px]">
              <p className="text-[20px] md:text-[28px] lg:text-[32px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans">
                Total Consumption (m³)
              </p>
              <p className="text-[16px] md:text-[24px] lg:text-[28px] tracking-[-0.04em] font-normal text-[#1e1f24] font-albert-sans">
                = Meter Reading × Correction Factor
              </p>
            </div>
          </div>
        </Reveal>

        {/* Separator */}
        <div className="w-full h-px bg-[#b9bbc6]" />

        {/* Sample Bill Breakdown */}
        <Reveal direction="up">
          <h2 className="text-[24px] md:text-[40px] lg:text-[54px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans leading-[1.1]">
            Example: Sample Residential Bill Breakdown
          </h2>
        </Reveal>

        <Reveal direction="up">
          <div className="flex flex-col gap-4 md:gap-5">
            {/* Mobile Cards */}
            <div className="flex flex-col gap-3 md:hidden">
              {billBreakdown.map((row) => (
                <div
                  key={row.component}
                  className="rounded-[12px] border border-[#62636C33] p-4 flex items-center justify-between gap-3 hover:border-[#122d63]/15 hover:shadow-[0_4px_16px_rgba(18,45,99,0.08)] transition-all duration-300"
                >
                  <div className="min-w-0">
                    <p className="text-[14px] font-semibold text-[#1c1c1c] font-albert-sans tracking-[-0.04em]">
                      {row.component}
                    </p>
                    <p className="text-[12px] text-[#62636c] font-albert-sans tracking-[-0.04em] mt-0.5">
                      {row.description}
                    </p>
                  </div>
                  <p className="text-[16px] font-semibold text-[#f24d2b] font-albert-sans tracking-[-0.04em] shrink-0">
                    {row.example}
                  </p>
                </div>
              ))}
              <div className="rounded-[12px] bg-[#f6f6f9] p-4 flex items-center justify-between">
                <p className="text-[16px] font-semibold text-[#1c1c1c] font-albert-sans tracking-[-0.04em]">
                  Total Payable
                </p>
                <p className="text-[16px] font-semibold text-[#1c1c1c] font-albert-sans tracking-[-0.04em]">
                  414.75 AED
                </p>
              </div>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block rounded-t-[16px] border border-[#62636C33] overflow-hidden">
              <div className="bg-[#f24d2b] grid grid-cols-3">
                {billHeaders.map((header) => (
                  <div
                    key={header}
                    className="py-[12px] px-[15px] border-r border-white last:border-r-0"
                  >
                    <p className="text-[20px] lg:text-[28px] tracking-[-0.04em] font-medium text-white font-albert-sans leading-[40px]">
                      {header}
                    </p>
                  </div>
                ))}
              </div>
              {billBreakdown.map((row) => (
                <div
                  key={row.component}
                  className="grid grid-cols-3 border-t border-[#62636C33] hover:bg-[#f8f9fb] transition-colors duration-200"
                >
                  {[row.component, row.description, row.example].map(
                    (cell, i) => (
                      <div
                        key={i}
                        className="py-[8px] px-[15px] border-r border-[#62636C33] last:border-r-0"
                      >
                        <p className="text-base lg:text-[24px] tracking-[-0.04em] leading-[32px] text-[#62636c] font-albert-sans">
                          {cell}
                        </p>
                      </div>
                    )
                  )}
                </div>
              ))}
              <div className="grid grid-cols-3 bg-[#f6f6f9] border-t border-[#62636C33]">
                <div className="col-span-2 py-[28px] px-[16px] border-r border-[#62636C33]">
                  <p className="text-[24px] lg:text-[28px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans">
                    Total Payable
                  </p>
                </div>
                <div className="py-[28px] px-[16px]">
                  <p className="text-[24px] lg:text-[28px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans">
                    414.75 AED
                  </p>
                </div>
              </div>
            </div>

            {/* Tip Box */}
            <div className="rounded-[12px] md:rounded-[16px] bg-gradient-to-r from-[#fff9f0] to-[#fff5e6] border border-[#f5a623]/20 px-4 md:px-[48px] py-4 md:py-[24px] flex items-start gap-3 md:gap-4">
              <div className="w-9 h-9 md:w-10 md:h-10 rounded-[10px] bg-[#f5a623]/15 text-[#c08415] flex items-center justify-center shrink-0 mt-0.5">
                <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 md:w-6 md:h-6">
                  <path
                    d="M12 2a7 7 0 0 1 7 7c0 2.38-1.19 4.47-3 5.74V17a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-2.26C6.19 13.47 5 11.38 5 9a7 7 0 0 1 7-7zM9 21h6M10 21v1a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-1"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <div className="flex flex-col gap-1 md:gap-2.5">
                <p className="text-[18px] md:text-[24px] lg:text-[28px] tracking-[-0.04em] font-semibold text-[#1c1c1c] font-albert-sans">
                  Tip:
                </p>
                <p className="text-[14px] md:text-base lg:text-[28px] tracking-[-0.04em] font-normal text-[#1e1f24] font-albert-sans">
                  Submit your meter reading monthly to ensure accurate billing and
                  avoid estimates.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
